import { useMemo } from 'react'
import firebase from '../../firebase/compat'
import { v4 as uuid } from 'uuid'
import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import useSettings from '../../hooks/Settings'
import positionAtTop from '../../helpers/dashboard/positionAtTop'
import {
    STATIC_CARD_COMPONENTS,
    STATIC_CARD_LABELS,
    STATIC_CARD_PILLS,
    isStaticCardVisible,
} from './staticCardRegistry'

/**
 * @typedef {object} StaticCardOption
 * @property {string} type - Static card type key from the registry
 * @property {string} label - Human-readable card name
 * @property {boolean} isActive - True when the card is currently on the dashboard
 * @property {string | null} existingDocId - Soft-deleted DashboardItems doc to restore, if any
 * @property {{ variant: string, label: string } | null} pill - Category pill matching the card's own header, if any
 */

// Computes the catalog of registered static cards visible to this client,
// flagging which ones are currently active on the dashboard. Returns the
// catalog + a restore handler that either un-soft-deletes or creates a fresh
// record, matching functions/dashboard/createBasicDashboard.js.
const useStaticCards = (allItems) => {
    const settings = useSettings()

    /** @type {Array<StaticCardOption>} */
    const staticCards = useMemo(() => {
        const itemsArray = Array.isArray(allItems) ? allItems : []

        const activeStaticTypes = new Set(
            itemsArray
                .filter((item) => item?.Static && item?.Deleted !== 'true' && item?.Deleted !== true)
                .map((item) => item.Type)
        )

        return Object.keys(STATIC_CARD_COMPONENTS)
            .filter((type) => isStaticCardVisible(type, client, settings))
            .map((type) => {
                const existing = itemsArray.find((item) =>
                    item?.Type === type
                    && item?.Static
                    && (item?.Deleted === 'true' || item?.Deleted === true)
                )
                return {
                    type,
                    label: existing?.Label || STATIC_CARD_LABELS[type] || type,
                    isActive: activeStaticTypes.has(type),
                    existingDocId: existing?.docid || null,
                    pill: STATIC_CARD_PILLS[type] || null,
                }
            })
    }, [allItems, settings])

    const restoreCard = async ({ type, existingDocId }) => {
        // Bovenaan, niet achteraan: een kaart die je net toevoegt hoort in beeld
        // te staan (testronde 2026-10-13, B7). Geldt ook voor terugzetten — in
        // 'Aanpassen' is dat voor de gebruiker hetzelfde als toevoegen.
        const snapshot = await db.collection('DashboardItems')
            .where('CompagnyID', '==', client)
            .where('Version', '==', 2)
            .get()
        const topPosition = positionAtTop(snapshot.docs.map((d) => d.data().Position))

        if (existingDocId) {
            await db.collection('DashboardItems').doc(existingDocId).update({
                Deleted: 'false',
                Position: topPosition,
                Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            })
            return
        }

        await db.collection('DashboardItems').add({
            ID: uuid(),
            CompagnyID: client,
            Compagny: client,
            Version: 2,
            Position: topPosition,
            Type: type,
            Static: true,
            Deleted: 'false',
            DeletedAt: '',
            Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        })
    }

    return { staticCards, restoreCard }
}

export default useStaticCards
